import React, { useCallback, useEffect, useMemo, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import WarningIcon from "@mui/icons-material/WarningRounded";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import {
    gridClasses,
    GridColDef,
    GridEventListener,
    GridInitialState,
    GridRenderCellParams,
    GridRowParams,
    GRID_DETAIL_PANEL_TOGGLE_FIELD,
    useGridApiRef,
    GridGroupingColDefOverride,
    GridSortModel,
    GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
    GRID_AGGREGATION_ROOT_FOOTER_ROW_ID,
    GridValueGetterParams,
    GridDataGroupNode,
    GridAutoGeneratedGroupNode,
    GridTreeNode,
    GridRowId,
    GridGroupNode,
    GridSortCellParams,
    GridComparatorFn,
    GridValueFormatterParams,
} from "@mui/x-data-grid-premium";

import useEmployeeUiState from "../../../hooks/useEmployeeUiState";
import { StripedDataGrid } from "../muiDataGrid";
import { CustomToolbar } from "./customToolbar";
import _groupBy from "lodash/groupBy";
import _map from "lodash/map";
import _forEach from "lodash/forEach";
import { Base } from "../../../framework/base";
import { useTranslation } from "react-i18next";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";

const getGroupingModeMultipleColumnName = (name: string) =>
    `__row_group_by_columns_group_${name}__`;

const getFieldNameFromGroupingColumn = (name: string) =>
    (name ?? "").split("_").filter(Boolean).reverse()[0];

function getAllChildRowIds(
    rowNode: GridTreeNode | GridDataGroupNode | GridAutoGeneratedGroupNode,
    api: GridApiPremium
): GridRowId[] {
    if (rowNode.type === "group") {
        return rowNode.children
            .map((c) => api.getRowNode(c))
            .flatMap((n) => getAllChildRowIds(n, api));
    }

    return [rowNode.id];
}

export function getAllChildRows<T>(
    rowNode: GridTreeNode | GridDataGroupNode | GridAutoGeneratedGroupNode,
    api: GridApiPremium
): T[] {
    return getAllChildRowIds(rowNode, api)
        .map((id) => api.getRow<GroupedRow<T>>(id))
        .flatMap((r) => r?.rows ?? []);
}

export interface GroupableDataGridRow {
    id: string;
    totals: Record<string, number>;
}

export interface GroupedRow<T> {
    id: string;
    warning?: string; // Generic field for displaying warning in the grouping col
    rows: (T & GroupableDataGridRow)[];
    totals: Record<string, number>;
}

interface DataGridProps<T> {
    noRowsText: string;
    detailsComponent: (props: any) => JSX.Element;
    detailsComponentProps?: any;
    columns: GridColDef[];
    rows: (T & GroupableDataGridRow)[];
    groupingColDef: GridGroupingColDefOverride;
    getGroupLabel?: (value: any, field: string) => string;
    persistStateKey?: string;
    pinnedColumns?: string[];
    hiddenColumns?: string[];
    enableColumnSelector?: boolean;
    groupBy: (keyof T)[];
    groupingModeMultiple?: boolean;
    showGroupedRows?: boolean;
}

interface GroupedDataGridState {
    hideEmptyColumns: boolean;
    hiddenColumns: string[];
}

export function GroupableDataGrid<T>({
    noRowsText,
    detailsComponent: DetailsComponent,
    detailsComponentProps = {},
    columns,
    rows,
    groupingColDef,
    getGroupLabel,
    persistStateKey,
    pinnedColumns,
    hiddenColumns: propHiddenColumns,
    enableColumnSelector = false,
    groupBy: propGroupBy,
    groupingModeMultiple: propGroupingModeMultiple = false,
    showGroupedRows = true,
}: DataGridProps<T>) {
    const {
        value: persistedGridState,
        setValue: setPersistedGridState,
        initialized: gridStateInitialized,
    } = useEmployeeUiState<GridInitialState & GroupedDataGridState>(
        persistStateKey,
        { hideEmptyColumns: true, hiddenColumns: [] }
    );

    const { t } = useTranslation();

    useEffect(() => {
        if (gridStateInitialized) {
            if (persistedGridState?.current.hideEmptyColumns !== undefined) {
                setHideEmptyColumns(
                    persistedGridState?.current.hideEmptyColumns
                );
            }

            setHiddenColumns(persistedGridState?.current.hiddenColumns ?? []);

            if (!!persistedGridState?.current?.sorting?.sortModel) {
                setSortModel(persistedGridState.current.sorting.sortModel);
            }
        }
    }, [gridStateInitialized]);

    const [hideEmptyColumns, setHideEmptyColumns] = useState(true);
    const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
    const [sortModel, setSortModel] = useState<GridSortModel>();

    const groupedRows: GroupedRow<T>[] = useMemo(
        () =>
            _map(
                _groupBy(
                    rows,
                    (r) => propGroupBy?.map((g) => r[g]).join("_") ?? null
                ),
                (arr, groupingKey) => ({
                    ...Object.fromEntries(
                        _map(
                            arr[0] ?? ({} as T & GroupableDataGridRow),
                            (v, k) =>
                                typeof v === "object"
                                    ? null
                                    : [
                                        k,
                                        Base.getUniqueItems(
                                            arr.map(
                                                (a) =>
                                                    a[
                                                          k as keyof (T &
                                                              GroupableDataGridRow)
                                                    ]
                                            )
                                        ).length === 1
                                            ? v
                                            : null,
                                    ]
                        ).filter(Boolean)
                    ),
                    ...Object.fromEntries(
                        propGroupBy?.map((g, i) => [
                            g,
                            groupingKey.split("_")[i] ?? null,
                        ]) ?? []
                    ),
                    id: groupingKey,
                    rows: arr,
                    totals: arr.reduce((acc, curr) => {
                        _forEach(curr.totals, (v, k) => {
                            acc[k] ??= 0;
                            acc[k] += v;
                        });

                        return acc;
                    }, {}),
                })
            ),
        [rows, propGroupBy]
    );

    const groupBy = useMemo(
        () =>
            !showGroupedRows
                ? []
                : ((!!propGroupBy
                    ? propGroupBy.slice(0, -1)
                    : propGroupBy) as unknown as string[]),
        [propGroupBy, showGroupedRows]
    );

    // Add Footer row rendering to first column when 'showGropedRows' is false
    // This is done here because grouping is disabled when grouped rows are hidden.
    const columnDef = useMemo(
        () =>
            showGroupedRows
                ? columns
                : columns.map((c) => {
                    if (!!c.field && c.field === propGroupBy?.[0]) {
                        return {
                            ...c,
                            valueFormatter: (
                                params: GridValueFormatterParams<unknown>
                            ) => {
                                if (
                                    params.id ===
                                      GRID_AGGREGATION_ROOT_FOOTER_ROW_ID
                                ) {
                                    return t("common.total").toString();
                                }

                                if (!!c.valueFormatter) {
                                    return c.valueFormatter(
                                        params
                                    ) as unknown;
                                }

                                return params.value;
                            },
                            renderCell: (params: GridRenderCellParams) => {
                                if (
                                    !!params.formattedValue &&
                                      params?.rowNode?.type === "pinnedRow"
                                ) {
                                    return (
                                        <div className="font-weight-bold">
                                            {params.formattedValue}
                                        </div>
                                    );
                                }
                                if (!!c.renderCell) {
                                    return c.renderCell(params);
                                }

                                return params.formattedValue as unknown;
                            },
                        } as GridColDef<GroupedRow<T>>;
                    }

                    return c;
                }),
        [columns, showGroupedRows, propGroupBy, t]
    );

    const apiRef = useGridApiRef();

    const persistGridState = useCallback(() => {
        if (apiRef?.current?.exportState) {
            const currentState = apiRef.current.exportState();
            setPersistedGridState({
                columns: {
                    orderedFields: currentState.columns.orderedFields,
                },
                hideEmptyColumns,
                hiddenColumns,
                sorting: {
                    sortModel,
                },
            });
        }
    }, [apiRef, hideEmptyColumns, hiddenColumns, sortModel]);

    useEffect(() => {
        persistGridState();
    }, [persistGridState]);

    const emptyColumns = useMemo(() => {
        if (!groupedRows) {
            // When rows gets loaded later than columns
            return [];
        }
        return (
            columns
                ?.filter(
                    (c) =>
                        !groupedRows.some(
                            (r) => r[c.field] ?? r.totals[c.field]
                        )
                )
                .map((c) => c.field) ?? []
        );
    }, [groupedRows, columns]);

    // If grouped by only one column,
    // make sure groupingModeMultiple is true to render grouping column correctly
    const groupingModeMultiple = useMemo(
        () => propGroupingModeMultiple || groupBy?.length === 0,
        [propGroupingModeMultiple, groupBy]
    );

    // Hidden because of grouping or as a propHiddenColumn
    const nonVisibleColumns = useMemo(
        () =>
            [
                propHiddenColumns ?? [],
                propHiddenColumns?.map((c) =>
                    getGroupingModeMultipleColumnName(c)
                ) ?? [],
                groupingModeMultiple ? groupBy : (propGroupBy as string[]),
            ].flat(),
        [groupingModeMultiple, groupBy, propGroupBy, propHiddenColumns]
    );

    const columnVisibility = useMemo(
        () => ({
            ...Object.fromEntries(
                columns.map((c) => [
                    c.field,
                    nonVisibleColumns.includes(c.field) ||
                    hiddenColumns.includes(c.field)
                        ? false
                        : !hideEmptyColumns || !emptyColumns.includes(c.field),
                ])
            ),
            ...Object.fromEntries(
                groupBy.map((g) => {
                    const field = getGroupingModeMultipleColumnName(g);

                    return [
                        field,
                        !(
                            nonVisibleColumns.includes(field) ||
                            hiddenColumns.includes(field)
                        ),
                    ];
                })
            ),
        }),
        [
            columns,
            groupBy,
            hideEmptyColumns,
            emptyColumns,
            hiddenColumns,
            nonVisibleColumns,
        ]
    );

    const hideableColumns = useMemo(
        () =>
            columns.filter(
                (c) =>
                    !nonVisibleColumns.includes(c.field) && c.hideable !== false
            ),
        [columns, nonVisibleColumns]
    );

    const columnVisibilityModel = {
        [GRID_DETAIL_PANEL_TOGGLE_FIELD]: false,
        ...columnVisibility,
    };

    const aggregationModel = useMemo(
        () =>
            Object.fromEntries([
                ...columns.map((c) => [c.field, "sum"]),
            ]) as Record<string, string>,
        [columns]
    );

    const pinnedColumnsModel = useMemo(
        () => ({
            left: [
                GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
                ...((propGroupBy as string[]) ?? []).flatMap((g) => [
                    getGroupingModeMultipleColumnName(g),
                    g,
                ]),
                ...(pinnedColumns ?? []),
            ],
        }),
        [GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD, propGroupBy, pinnedColumns]
    );

    useEffect(() => {
        setSortModel(
            !!propGroupBy?.[0]
                ? [
                    {
                        field:
                              !showGroupedRows || groupBy?.length === 0
                                  ? (propGroupBy[0] as string)
                                  : groupingModeMultiple
                                      ? getGroupingModeMultipleColumnName(
                                        propGroupBy[0] as string
                                      )
                                      : GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
                        sort: "asc",
                    },
                ]
                : undefined
        );
    }, [
        propGroupBy?.[0],
        groupingModeMultiple,
        showGroupedRows,
        groupBy?.length,
    ]);

    const initialState: GridInitialStatePremium = {
        aggregation: {
            model: aggregationModel,
        },
        columns: {
            ...persistedGridState?.current?.columns,
            columnVisibilityModel,
        },
        sorting: {
            sortModel,
        },
        rowGrouping: {
            model: groupBy,
        },
        pinnedColumns: pinnedColumnsModel,
    };

    const onRowClick: GridEventListener<"rowClick"> = (params) => {
        const rowNode = apiRef.current.getRowNode(params.id);
        if (!rowNode) return;

        if (rowNode.type === "group") {
            apiRef.current.setRowChildrenExpansion(
                params.id,
                !rowNode.childrenExpanded
            );
        } else if (rowNode.type === "leaf") {
            apiRef.current.toggleDetailPanel(params.id);
        }
    };

    const getDetailPanelContent = useCallback(
        (params: GridRowParams) => {
            return (
                <DetailsComponent row={params.row} {...detailsComponentProps} />
            );
        },
        [DetailsComponent, detailsComponentProps]
    );

    const leafField = useMemo(
        () =>
            !!propGroupBy
                ? (propGroupBy[propGroupBy.length - 1] as string)
                : undefined,
        [propGroupBy]
    );

    const groupedColumnSortComparator = useCallback(
        (
            v1: any,
            v2: any,
            param1: GridSortCellParams<any>,
            param2: GridSortCellParams<any>
        ) => {
            let comparator: GridComparatorFn<any>;

            if (param1.rowNode.type === "group") {
                comparator = columns.find(
                    (c) =>
                        c.field ===
                        (
                            param1.rowNode as
                                | GridDataGroupNode
                                | GridAutoGeneratedGroupNode
                        ).groupingField
                )?.sortComparator;
            } else if (param1.rowNode.type === "leaf") {
                comparator = columns.find(
                    (c) => c.field === leafField
                )?.sortComparator;
            }

            if (!!comparator) {
                return comparator(v1, v2, param1, param2);
            }
        },
        [columns, leafField]
    );

    if (!gridStateInitialized) return null;

    return (
        <StripedDataGrid
            disableRowGrouping={!showGroupedRows}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            columnVisibilityModel={columnVisibilityModel}
            onColumnOrderChange={persistGridState}
            initialState={initialState}
            slots={{
                noRowsOverlay: () => (
                    <Alert severity="info" sx={{ p: 2 }}>
                        {noRowsText}
                    </Alert>
                ),
                toolbar: CustomToolbar,
            }}
            slotProps={{
                toolbar: {
                    hideEmptyColumns,
                    setHideEmptyColumns,
                    emptyColumns,
                    columns: hideableColumns,
                    hiddenColumns,
                    setHiddenColumns,
                    enableColumnSelector,
                    disableExpandAll: !showGroupedRows,
                },
            }}
            apiRef={apiRef}
            onRowClick={onRowClick}
            disableColumnMenu
            rowSelection={false}
            density="compact"
            hideFooter
            columns={columnDef}
            rows={groupedRows}
            disableColumnResize
            rowGroupingColumnMode={groupingModeMultiple ? "multiple" : "single"}
            rowGroupingModel={groupBy}
            groupingColDef={{
                hideDescendantCount: true,
                ...(groupingModeMultiple
                    ? {
                        valueGetter: (
                            params: GridValueGetterParams<
                                  GroupedRow<T>,
                                  string
                              >
                        ) => {
                            if (
                                params?.id ===
                                  GRID_AGGREGATION_ROOT_FOOTER_ROW_ID
                            )
                                return;

                            if (params?.rowNode?.type === "group") {
                                const isGroupedCol =
                                      getGroupingModeMultipleColumnName(
                                          params.rowNode.groupingField
                                      ) === params.field;

                                if (isGroupedCol) {
                                    return params.rowNode.groupingKey;
                                }
                            }

                            if (
                                !!params.rowNode.parent &&
                                  params.rowNode.depth > 0
                            ) {
                                const api = params.api as GridApiPremium;
                                const ids = api.getRowGroupChildren({
                                    groupId: params.rowNode.parent,
                                });
                                const field = getFieldNameFromGroupingColumn(
                                    params?.field
                                );
                                const row: GroupedRow<T> = api.getRow(ids[0]);

                                return row[field as keyof GroupedRow<T>];
                            }
                        },
                    }
                    : {
                        leafField,
                    }),
                valueFormatter: (data) => {
                    // If footer row and first or only grouping column
                    if (
                        data?.id === GRID_AGGREGATION_ROOT_FOOTER_ROW_ID &&
                        (data?.field ===
                            GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD ||
                            data?.field ===
                                getGroupingModeMultipleColumnName(
                                    (propGroupBy ?? [])[0] as string
                                ))
                    ) {
                        return t("common.total").toString();
                    }

                    const formatter = columns.find(
                        (c) => c.field === leafField
                    )?.valueFormatter;

                    if (!!formatter) {
                        return formatter(data) as unknown;
                    }

                    const value: string | undefined = data?.value;

                    return value;
                },
                width: groupingModeMultiple ? 150 : 250,
                sortComparator: groupedColumnSortComparator,
                renderCell: (params: GridRenderCellParams<GroupedRow<T>>) => (
                    <GroupingCol
                        params={params}
                        getGroupLabel={getGroupLabel}
                        groupingModeMultiple={groupingModeMultiple}
                        groupBy={propGroupBy ?? []}
                        leafField={leafField}
                    />
                ),
                ...(groupingModeMultiple ? {} : groupingColDef),
            }}
            pinnedColumns={pinnedColumnsModel}
            getDetailPanelContent={
                !!DetailsComponent ? getDetailPanelContent : undefined
            }
            getDetailPanelHeight={() => "auto"}
            getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            sx={{
                maxHeight: "100%",
                height: "initial",
                width: "100%",
                // Hide the "sum" label on headers
                [`& .${gridClasses.aggregationColumnHeaderLabel}`]: {
                    display: "none",
                },
                // disable cell selection style
                ".MuiDataGrid-cell:focus": {
                    outline: "none",
                },
                "& .MuiDataGrid-row:hover": {
                    cursor: "pointer",
                },
                // This is needed to make sticky positioning work for the detail panel
                "& .MuiDataGrid-detailPanel": {
                    overflow: "visible",
                },
                // Needed to display "no rows" overlay correctly
                ".MuiDataGrid-overlayWrapper": {
                    height: "auto !important",
                },
                ".MuiDataGrid-overlayWrapperInner": {
                    height: "auto !important",
                },
            }}
        />
    );
};

interface GroupingColProps<T> {
    params: GridRenderCellParams<GroupedRow<T>>;
    getGroupLabel: (value: any, field: string) => string;
    groupingModeMultiple: boolean;
    groupBy: (keyof T)[];
    leafField?: string;
}

export function GroupingCol<T>(props: GroupingColProps<T>) {
    const { params, getGroupLabel, groupingModeMultiple, groupBy, leafField } =
        props;
    const { id, value, row, formattedValue, api } = params;

    const warning = !!id ? api.getCellValue(id, "warning") : null;
    const warningIcon = !!warning ? (
        <Tooltip title={warning}>
            <WarningIcon color="orange" sx={{ mr: 0.5 }} />
        </Tooltip>
    ) : null;

    if (params?.rowNode?.type === "group") {
        const field = groupingModeMultiple
            ? getFieldNameFromGroupingColumn(params?.field)
            : params.rowNode.groupingField;

        const isFirstGroupingCol =
            !groupingModeMultiple || groupBy[0] === field;

        return (
            <GroupToggle
                params={params}
                groupingField={field}
                warningIcon={isFirstGroupingCol ? warningIcon : null}
                label={
                    (!!getGroupLabel ? getGroupLabel(value, field) : value) ||
                    "-"
                }
            />
        );
    } else if (params?.rowNode?.type === "leaf") {
        const field = groupingModeMultiple
            ? getFieldNameFromGroupingColumn(params?.field)
            : leafField;
        const label =
            (groupingModeMultiple || !formattedValue) && getGroupLabel
                ? getGroupLabel(value ?? row.rows[0][field], field)
                : (formattedValue as string);

        const isFirstGroupingCol =
            !groupingModeMultiple || groupBy[0] === field;

        return (
            <GroupLabel
                showToggle={false}
                marginLeft={
                    !!warning
                        ? 0
                        : groupingModeMultiple
                            ? 2
                            : params?.rowNode?.depth * 2
                }
                warningIcon={isFirstGroupingCol ? warningIcon : null}
                label={label}
            />
        );

    } else if (!!formattedValue && params?.rowNode?.type === "pinnedRow") {
        return <div className="font-weight-bold">{formattedValue}</div>;
    }
};

interface GroupToggleProps<T> {
    params: GridRenderCellParams<GroupedRow<T>>;
    groupingField?: string;
    label: string;
    marginLeft?: number;
    warningIcon?: React.JSX.Element;
}

export function GroupToggle<T>(props: GroupToggleProps<T>) {
    const { params, groupingField, label, marginLeft = 0, warningIcon } = props;
    const rowNode = params.rowNode as GridGroupNode;

    return (
        <GroupLabel
            showToggle={
                groupingField ===
                rowNode.groupingField
            }
            open={rowNode.childrenExpanded ?? false}
            marginLeft={marginLeft}
            warningIcon={warningIcon}
            label={label ?? "-"}
        />
    );
};

interface GroupLabelProps {
    label: string;
    marginLeft?: number;
    warningIcon?: React.JSX.Element;
    showToggle: boolean;
    open?: boolean;
}

export function GroupLabel(props: GroupLabelProps) {
    const { showToggle, open, label, marginLeft = 0, warningIcon } = props;

    return (
        <Box
            ml={!showToggle ? marginLeft * 2 : 0}
            className="text-truncate"
        >
            {showToggle ? (
                open ? (
                    <KeyboardArrowDownIcon sx={{ marginLeft }} />
                ) : (
                    <KeyboardArrowRightIcon sx={{ marginLeft }} />
                )
            ) : null}
            {warningIcon}
            <Box display="inline" title={label}>
                {label ?? ""}
            </Box>
        </Box>
    );
};